import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import AutoAwesomeMosaicOutlinedIcon from '@mui/icons-material/AutoAwesomeMosaicOutlined';
import { DatePicker } from '../../components/common/DatePicker';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ForgeApiError } from '../../services/ForgeApiService';
import { ForgeApiService } from '../../services/ForgeApiService';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../../utils/safeStorage';
import {
  DailyReportBarcodeExample,
  DailyReportCoverageMetric,
  DailyReportMetric,
  DailyReportNamedMetric,
  DailyReportSectionSplit,
  DailyReportSnapshot,
  DailyReportStoreRow,
} from '../../types';
import { ApiService } from '../../../services/api';
import { Store } from '../../../types';

const CHAIN_STORAGE_KEY = 'forge_daily_report_chain_id';
const SUPPORTED_CHAIN_IDS = new Set<number>([46]);
const DEFAULT_ESTIMATED_DURATION_MS = 45000;

type ChainOption = {
  chainId: number;
  chainName: string;
  timeZone: string;
};

type StoreSortKey = 'storeName' | 'eligibleMods' | 'whitelistedMods' | 'whitelistedModPct';

const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)' };

function pad(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

function getTodayYmd(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatCoverage(metric: DailyReportCoverageMetric): string {
  return `${formatPercent(metric.pct)} (${formatNumber(metric.covered)}/${formatNumber(metric.total)})`;
}

function formatMetricShare(metric: DailyReportMetric): string {
  return `${formatNumber(metric.count)} · ${formatPercent(metric.pct)}`;
}

function formatRelativeAge(ageSeconds: number): string {
  if (ageSeconds < 60) return 'computed just now';
  if (ageSeconds < 3600) return `computed ${Math.floor(ageSeconds / 60)}m ago`;
  if (ageSeconds < 86400) return `computed ${Math.floor(ageSeconds / 3600)}h ago`;
  return `computed ${Math.floor(ageSeconds / 86400)}d ago`;
}

function formatTimestampUtc(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(value));
}

function formatTimestampInTimeZone(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(new Date(value));
}

function getYmdInTimeZone(value: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  return `${year}-${month}-${day}`;
}

function formatCurrentTimeInTimeZone(value: Date, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone,
  }).format(value);
}

function formatDurationFromSeconds(totalSeconds: number): string {
  if (totalSeconds < 60) return `${Math.max(totalSeconds, 1)}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${pad(seconds)}s`;
}

function getFreshnessTone(ageSeconds: number): { color: string; background: string; border: string } {
  if (ageSeconds < 4 * 3600) {
    return {
      color: 'var(--positive)',
      background: 'var(--positive-subtle)',
      border: 'var(--accent-border)',
    };
  }
  if (ageSeconds <= 24 * 3600) {
    return {
      color: 'var(--warning)',
      background: 'var(--warning-subtle)',
      border: 'rgba(210,153,34,0.28)',
    };
  }
  return {
    color: 'var(--danger)',
    background: 'var(--danger-subtle)',
    border: 'rgba(248,81,73,0.28)',
  };
}

function loadStoredChainId(): number | null {
  const stored = safeLocalStorageGetItem(CHAIN_STORAGE_KEY);
  if (!stored) return null;
  const parsed = Number(stored);
  return Number.isFinite(parsed) ? parsed : null;
}

function saveStoredChainId(chainId: number): void {
  safeLocalStorageSetItem(CHAIN_STORAGE_KEY, String(chainId));
}

function durationStorageKey(chainId: number): string {
  return `forge_daily_report_estimated_duration_ms:${chainId}`;
}

function loadChainDurationMs(chainId: number): number | null {
  const stored = safeLocalStorageGetItem(durationStorageKey(chainId));
  if (!stored) return null;
  const parsed = Number(stored);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function saveChainDurationMs(chainId: number, value: number): void {
  safeLocalStorageSetItem(durationStorageKey(chainId), String(Math.round(value)));
}

function estimateDurationMs(lastDurationMs: number | null | undefined): number {
  if (!lastDurationMs || lastDurationMs <= 0) {
    return DEFAULT_ESTIMATED_DURATION_MS;
  }
  return Math.round(lastDurationMs * 1.1);
}

const controlStyle: React.CSSProperties = {
  appearance: 'none',
  WebkitAppearance: 'none',
  backgroundColor: 'var(--surface)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '0 34px 0 12px',
  height: 32,
  fontSize: 13,
  cursor: 'pointer',
  outline: 'none',
  minWidth: 180,
};

const actionButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  borderRadius: 6,
  border: '1px solid var(--accent-border)',
  background: 'var(--accent-subtle)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  padding: '0 14px',
  height: 32,
  fontSize: 13,
  fontWeight: 600,
};

const metricCardStyle: React.CSSProperties = {
  padding: 18,
  minHeight: 126,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  background:
    'linear-gradient(180deg, color-mix(in srgb, var(--surface) 88%, var(--accent-subtle) 12%), var(--surface))',
};

const sectionGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 18,
};

const tableHeaderButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  padding: 0,
  font: 'inherit',
  fontWeight: 600,
};

const badgeStyle = (ageSeconds: number): React.CSSProperties => {
  const tone = getFreshnessTone(ageSeconds);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    borderRadius: 999,
    padding: '6px 12px',
    border: `1px solid ${tone.border}`,
    background: tone.background,
    color: tone.color,
    fontSize: 12,
    fontWeight: 700,
  };
};

const sparkDotStyle = (background: string): React.CSSProperties => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  background,
  display: 'inline-block',
});

const tooltipSlotProps = {
  tooltip: {
    sx: {
      backgroundColor: 'var(--surface)',
      backgroundImage: 'none',
      opacity: 1,
      color: 'var(--text-primary)',
      border: '1px solid color-mix(in srgb, var(--border) 82%, var(--accent-border) 18%)',
      boxShadow: '0 18px 42px rgba(0,0,0,0.16), 0 6px 18px rgba(0,0,0,0.10)',
      borderRadius: '12px',
      fontFamily: 'var(--font-body)',
      fontSize: 12.5,
      fontWeight: 500,
      letterSpacing: '-0.01em',
      maxWidth: 240,
      padding: '11px 13px',
    },
  },
  popper: {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ],
  },
};

const tooltipTextStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  lineHeight: 1.38,
  fontFamily: 'var(--font-body)',
  letterSpacing: '-0.01em',
};

const infoTriggerStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '1px solid color-mix(in srgb, var(--border) 84%, var(--accent-border) 16%)',
  background: 'color-mix(in srgb, var(--surface) 84%, var(--accent-subtle) 16%)',
  color: 'var(--text-tertiary)',
  cursor: 'help',
  flexShrink: 0,
};

const glyphStyle: React.CSSProperties = {
  width: 18,
  height: 18,
  display: 'block',
};

const BarcodeGlyph: React.FC = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true" style={glyphStyle}>
    {[1, 3, 4, 6, 9, 10, 12, 14, 15, 17].map((x, index) => (
      <rect
        key={x}
        x={x}
        y={index % 3 === 0 ? 2 : 1}
        width={index % 2 === 0 ? 1 : 2}
        height={index % 4 === 0 ? 15 : 17}
        rx={0.5}
        fill="currentColor"
      />
    ))}
  </svg>
);

const ProductBoxGlyph: React.FC = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true" style={glyphStyle}>
    <rect x="2.5" y="5.5" width="15" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2.5 8.5h15" stroke="currentColor" strokeWidth="1.5" />
    <rect x="5.2" y="9.7" width="3.1" height="4.6" rx="0.8" fill="currentColor" opacity="0.92" />
    <rect x="10.2" y="8.8" width="4.2" height="5.5" rx="0.9" fill="currentColor" opacity="0.72" />
    <path d="M6 3.5h8l1.5 2H4.5L6 3.5Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const InsightPopover: React.FC<{
  content: React.ReactNode;
  children: React.ReactElement;
  placement?: 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'right' | 'left';
}> = ({ content, children, placement = 'top' }) => (
  <Tooltip
    title={<div style={tooltipTextStyle}>{content}</div>}
    placement={placement}
    enterDelay={90}
    leaveDelay={40}
    disableInteractive
    slotProps={tooltipSlotProps}
    TransitionProps={{ timeout: 160 }}
  >
    {children}
  </Tooltip>
);

const InfoHint: React.FC<{
  content: React.ReactNode;
  placement?: 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'right' | 'left';
}> = ({ content, placement }) => (
  <InsightPopover content={content} placement={placement ?? 'bottom-start'}>
    <span style={infoTriggerStyle}>
      <InfoOutlinedIcon style={{ fontSize: 12 }} />
    </span>
  </InsightPopover>
);

const SectionLabel: React.FC<{
  label: string;
  infoContent?: React.ReactNode;
}> = ({ label, infoContent }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    <span>{label}</span>
    {infoContent ? <InfoHint content={infoContent} /> : null}
  </div>
);

const ProgressBanner: React.FC<{
  elapsedMs: number;
  estimatedDurationMs: number;
}> = ({ elapsedMs, estimatedDurationMs }) => {
  const elapsedSeconds = Math.max(1, Math.round(elapsedMs / 1000));
  const estimatedSeconds = Math.max(1, Math.round(estimatedDurationMs / 1000));
  const etaSeconds = Math.max(0, Math.round((estimatedDurationMs - elapsedMs) / 1000));
  const progressRatio =
    estimatedDurationMs > 0
      ? Math.min(elapsedMs / estimatedDurationMs, elapsedMs > estimatedDurationMs ? 0.96 : 0.92)
      : 0.2;
  const isOverEstimate = elapsedMs > estimatedDurationMs;

  return (
    <div
      style={{
        marginTop: 16,
        padding: '14px 16px',
        borderRadius: 12,
        border: '1px solid var(--accent-border)',
        background:
          'linear-gradient(135deg, color-mix(in srgb, var(--accent-subtle) 72%, var(--surface) 28%), color-mix(in srgb, var(--blue) 10%, var(--surface) 90%))',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Refreshing Snapshot
          </div>
          <div style={{ marginTop: 6, fontSize: 14, fontWeight: 700 }}>
            Recomputing the snapshot and replacing the cached latest version.
          </div>
          <div style={{ marginTop: 6, color: 'var(--text-secondary)', fontSize: 12 }}>
            The current snapshot stays visible while the new one is being written.
          </div>
        </div>
        <div style={{ display: 'grid', gap: 6, minWidth: 180 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
            <span style={{ color: 'var(--text-secondary)' }}>Elapsed</span>
            <span style={{ ...mono, fontWeight: 700 }}>{formatDurationFromSeconds(elapsedSeconds)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
            <span style={{ color: 'var(--text-secondary)' }}>
              {isOverEstimate ? 'Status' : 'Estimated left'}
            </span>
            <span style={{ ...mono, fontWeight: 700 }}>
              {isOverEstimate ? 'Running longer than estimate' : formatDurationFromSeconds(etaSeconds)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
            <span style={{ color: 'var(--text-secondary)' }}>Typical duration</span>
            <span style={{ ...mono, fontWeight: 700 }}>{formatDurationFromSeconds(estimatedSeconds)}</span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14, height: 10, background: 'color-mix(in srgb, var(--surface) 78%, var(--accent-subtle) 22%)', borderRadius: 999, overflow: 'hidden' }}>
        <div
          style={{
            width: `${Math.max(progressRatio * 100, 8)}%`,
            height: '100%',
            borderRadius: 999,
            background: isOverEstimate ? 'linear-gradient(90deg, var(--warning), var(--accent))' : 'linear-gradient(90deg, var(--accent), var(--blue))',
            transition: 'width 300ms ease',
          }}
        />
      </div>
    </div>
  );
};

const MetricTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
  infoContent?: React.ReactNode;
}> = ({ icon, label, value, detail, infoContent }) => (
  <div className="forge-card" style={metricCardStyle}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span>{label}</span>
        {infoContent ? <InfoHint content={infoContent} /> : null}
      </div>
      <div style={{ color: 'var(--accent)' }}>{icon}</div>
    </div>
    <div style={{ marginTop: 14, fontSize: 30, fontWeight: 800, lineHeight: 1.05 }}>{value}</div>
    <div style={{ marginTop: 10, color: 'var(--text-secondary)', fontSize: 12 }}>{detail}</div>
  </div>
);

const WhitelistedStoresTile: React.FC<{
  whitelistedStores: number;
  storesWithEligibleMods: number;
  totalActiveStores: number;
}> = ({
  whitelistedStores,
  storesWithEligibleMods,
  totalActiveStores,
}) => (
  <div className="forge-card" style={metricCardStyle}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span>Whitelisted Stores</span>
        <InfoHint
          content={
            <>
              <div><strong>Whitelisted store</strong></div>
              <div>An active store with at least one whitelisted mod.</div>
              <div>Eligible stores are active stores with at least one eligible mod in the current mod universe.</div>
            </>
          }
        />
      </div>
      <div style={{ color: 'var(--accent)' }}>
        <StorefrontOutlinedIcon />
      </div>
    </div>
    <div style={{ marginTop: 14, fontSize: 30, fontWeight: 800, lineHeight: 1.05 }}>{formatNumber(whitelistedStores)}</div>
    <div style={{ marginTop: 10, color: 'var(--text-secondary)', fontSize: 12 }}>
      {formatNumber(storesWithEligibleMods)} stores with eligible mods · {formatNumber(totalActiveStores)} active stores
    </div>
  </div>
);

const HistogramChart: React.FC<{
  data: DailyReportSnapshot['modHistogram'];
}> = ({ data }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;
    const updateWidth = () => {
      setContainerWidth(element.getBoundingClientRect().width);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const maxValue = Math.max(...data.map((bucket) => bucket.stores), 1);
  const chartHeight = 214;
  const chartWidth = Math.max(Math.round(containerWidth), 560);
  const padding = { top: 20, right: 18, bottom: 48, left: 60 };
  const innerHeight = chartHeight - padding.top - padding.bottom;
  const innerWidth = chartWidth - padding.left - padding.right;
  const gap = data.length > 1 ? Math.max(10, Math.min(18, innerWidth * 0.025)) : 0;
  const barWidth =
    data.length > 0
      ? Math.max(24, (innerWidth - gap * Math.max(data.length - 1, 0)) / Math.max(data.length, 1))
      : innerWidth;
  const getBarX = (index: number) => padding.left + index * (barWidth + gap);
  const getBarHeight = (stores: number) => (maxValue === 0 ? 0 : (stores / maxValue) * innerHeight);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (data.length === 0) {
      setHoverIndex(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const scale = chartWidth / rect.width;
    const svgX = localX * scale;
    if (svgX < padding.left || svgX > chartWidth - padding.right) {
      setHoverIndex(null);
      return;
    }
    const index = Math.min(
      data.length - 1,
      Math.max(0, Math.floor((svgX - padding.left) / (barWidth + gap))),
    );
    setHoverIndex(index);
  };

  const hoverBucket = hoverIndex === null ? null : data[hoverIndex];
  const hoverX = hoverIndex === null ? 0 : getBarX(hoverIndex) + barWidth / 2;

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', width: '100%' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoverIndex(null)}
    >
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        style={{ width: '100%', height: 238, display: 'block' }}
        role="img"
        aria-label="Histogram of whitelisted mod coverage by store"
      >
        <line
          x1={padding.left}
          y1={chartHeight - padding.bottom}
          x2={chartWidth - padding.right}
          y2={chartHeight - padding.bottom}
          stroke="var(--border)"
        />
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = chartHeight - padding.bottom - innerHeight * ratio;
          const label = Math.round(maxValue * ratio);
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                y1={y}
                x2={chartWidth - padding.right}
                y2={y}
                stroke="var(--border)"
                strokeDasharray={ratio === 0 ? undefined : '4 4'}
              />
              <text x={padding.left - 12} y={y + 4} fill="var(--text-secondary)" fontSize="11" textAnchor="end">
                {label}
              </text>
            </g>
          );
        })}
        {data.map((bucket, index) => {
          const height = getBarHeight(bucket.stores);
          const x = getBarX(index);
          const y = chartHeight - padding.bottom - height;
          const isHovered = hoverIndex === index;
          return (
            <g key={bucket.label}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={height}
                rx={10}
                fill="url(#histogramGradient)"
                opacity={isHovered ? 1 : 0.92}
                stroke={isHovered ? 'var(--blue)' : 'transparent'}
                strokeWidth={isHovered ? 1.5 : 0}
              />
              <text x={x + barWidth / 2} y={y - 8} fill="var(--text-primary)" fontSize="12" textAnchor="middle">
                {bucket.stores}
              </text>
              <text
                x={x + barWidth / 2}
                y={chartHeight - padding.bottom + 22}
                fill="var(--text-secondary)"
                fontSize="11"
                textAnchor="middle"
              >
                {bucket.label}
              </text>
            </g>
          );
        })}
        {hoverBucket && (
          <line
            x1={hoverX}
            y1={padding.top}
            x2={hoverX}
            y2={chartHeight - padding.bottom}
            stroke="var(--text-tertiary)"
            strokeDasharray="3 2"
            opacity="0.6"
          />
        )}
        <defs>
          <linearGradient id="histogramGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" />
            <stop offset="100%" stopColor="var(--blue)" />
          </linearGradient>
        </defs>
      </svg>
      {hoverBucket && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: hoverX / chartWidth > 0.72 ? undefined : `${(hoverX / chartWidth) * 100 + 2}%`,
            right: hoverX / chartWidth > 0.72 ? '6%' : undefined,
            transform: hoverX / chartWidth > 0.72 ? undefined : 'translateX(-10%)',
            pointerEvents: 'none',
            background:
              'linear-gradient(180deg, color-mix(in srgb, var(--surface) 82%, var(--elevated) 18%), color-mix(in srgb, var(--surface) 92%, var(--accent-subtle) 8%))',
            border: '1px solid color-mix(in srgb, var(--border) 82%, var(--accent-border) 18%)',
            borderRadius: 14,
            padding: '12px 14px',
            fontSize: 12,
            boxShadow: '0 18px 42px rgba(0,0,0,0.16), 0 6px 18px rgba(0,0,0,0.10)',
            minWidth: 200,
            zIndex: 2,
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-secondary)', fontWeight: 700 }}>
            Store coverage bucket
          </div>
          <div style={{ fontWeight: 700, marginTop: 4, marginBottom: 3 }}>{hoverBucket.label} whitelisted mods</div>
          <div style={{ color: 'var(--text-secondary)', lineHeight: 1.45 }}>
            {formatNumber(hoverBucket.stores)} stores in this bucket
          </div>
        </div>
      )}
    </div>
  );
};

const HorizontalMetricBars: React.FC<{
  items: DailyReportNamedMetric[];
  color: string;
}> = ({ items, color }) => {
  const maxValue = Math.max(...items.map((item) => item.count), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {items.map((item) => (
        <div key={item.name} style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 140px) 1fr auto', gap: 12, alignItems: 'center' }}>
          <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{item.name}</div>
          <div style={{ height: 10, background: 'var(--elevated)', borderRadius: 999, overflow: 'hidden' }}>
            <div
              style={{
                width: `${Math.max((item.count / maxValue) * 100, item.count > 0 ? 4 : 0)}%`,
                height: '100%',
                borderRadius: 999,
                background: color,
              }}
            />
          </div>
          <div style={{ ...mono, color: 'var(--text-secondary)', fontSize: 12 }}>
            {formatNumber(item.count)} · {formatPercent(item.pct)}
          </div>
        </div>
      ))}
    </div>
  );
};

const SourceBreakdown: React.FC<{
  items: DailyReportSnapshot['cropDiagnostics']['sourceDistribution'];
}> = ({ items }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    {items.map((item) => (
      <div key={item.source} style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--elevated)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div style={{ fontWeight: 700, textTransform: 'capitalize' }}>{item.source}</div>
          <div style={{ ...mono, color: 'var(--text-secondary)', fontSize: 12 }}>
            {formatNumber(item.count)} · {formatPercent(item.pct)}
          </div>
        </div>
        <div style={{ marginTop: 10, height: 9, background: 'var(--surface)', borderRadius: 999, overflow: 'hidden' }}>
          <div
            style={{
              width: `${Math.max(item.pct, item.count > 0 ? 2 : 0)}%`,
              height: '100%',
              borderRadius: 999,
              background:
                item.source === 'forge'
                  ? 'linear-gradient(90deg, var(--accent), var(--blue))'
                  : item.source === 'scan'
                    ? 'linear-gradient(90deg, var(--blue), var(--purple))'
                    : item.source === 'control'
                      ? 'linear-gradient(90deg, var(--warning), var(--blue))'
                      : 'linear-gradient(90deg, var(--text-secondary), var(--text-tertiary))',
            }}
          />
        </div>
        {item.forgeOriginSources.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {item.forgeOriginSources.map((origin) => (
              <div
                key={origin.name}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 8px',
                  borderRadius: 999,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-secondary)',
                  fontSize: 12,
                }}
              >
                <span style={sparkDotStyle('var(--accent)')} />
                <span>{origin.name}</span>
                <span style={mono}>{formatNumber(origin.count)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

const SectionSplitChart: React.FC<{
  sectionSplit: DailyReportSectionSplit;
}> = ({ sectionSplit }) => {
  const rows = [
    {
      key: 'design-front',
      label: 'Design · Front',
      metric: sectionSplit.designFrontProductCrops,
      color: 'linear-gradient(90deg, var(--blue), color-mix(in srgb, var(--blue) 72%, white 28%))',
    },
    {
      key: 'design-non-front',
      label: 'Design · Non-front',
      metric: sectionSplit.designNonFrontProductCrops,
      color: 'linear-gradient(90deg, color-mix(in srgb, var(--blue) 62%, var(--purple) 38%), var(--purple))',
    },
    {
      key: 'unassigned',
      label: 'Unassigned',
      metric: sectionSplit.unassignedProductCrops,
      color: 'linear-gradient(90deg, var(--warning), color-mix(in srgb, var(--warning) 65%, white 35%))',
    },
    {
      key: 'carton',
      label: 'Carton',
      metric: sectionSplit.cartonCrops,
      color: 'linear-gradient(90deg, color-mix(in srgb, var(--accent) 68%, var(--blue) 32%), var(--accent))',
    },
    {
      key: 'inactive',
      label: 'Inactive',
      metric: sectionSplit.inactiveCrops,
      color: 'linear-gradient(90deg, color-mix(in srgb, var(--text-secondary) 78%, white 22%), var(--text-secondary))',
    },
  ];

  return (
    <div data-testid="daily-report-section-split">
      <div
        style={{
          height: 16,
          borderRadius: 999,
          overflow: 'hidden',
          background: 'var(--elevated)',
          display: 'flex',
          marginBottom: 16,
        }}
      >
        {rows.map((row) => (
          <div
            key={row.key}
            title={`${row.label}: ${formatMetricShare(row.metric)}`}
            style={{
              width: `${row.metric.pct}%`,
              minWidth: row.metric.count > 0 ? 6 : 0,
              background: row.color,
            }}
          />
        ))}
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        {rows.map((row) => (
          <div
            key={row.key}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(160px, 190px) 1fr auto',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <div style={{ fontWeight: 600 }}>{row.label}</div>
            <div style={{ height: 10, background: 'var(--elevated)', borderRadius: 999, overflow: 'hidden' }}>
              <div
                style={{
                  width: `${Math.max(row.metric.pct, row.metric.count > 0 ? 2 : 0)}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: row.color,
                }}
              />
            </div>
            <div style={{ ...mono, color: 'var(--text-secondary)', fontSize: 12 }}>{formatMetricShare(row.metric)}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 12 }}>
        Total sectioned crops: <span style={mono}>{formatNumber(sectionSplit.totalSectionedCrops)}</span>
      </div>
    </div>
  );
};

const ExampleTable: React.FC<{
  title: string;
  rows: DailyReportBarcodeExample[];
}> = ({ title, rows }) => (
  <div className="forge-card" style={{ minWidth: 0 }}>
    <div className="forge-card-title">{title}</div>
    {rows.length === 0 ? (
      <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No examples in this bucket for the selected snapshot.</div>
    ) : (
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Barcode</th>
              <th>Candidate Item Numbers</th>
              <th>Crops</th>
              <th>Designs</th>
              <th>Stores</th>
              <th>Link</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${title}-${row.normalizedBarcode}`}>
                <td style={mono}>{row.normalizedBarcode}</td>
                <td style={mono}>{row.candidateItemNumbers.join(', ') || 'None'}</td>
                <td style={mono}>{formatNumber(row.cropCount)}</td>
                <td style={mono}>{formatNumber(row.designCount)}</td>
                <td>{row.sampleStoreNames.join(', ') || 'None'}</td>
                <td>
                  {row.masterLabelItemNumber ? (
                    <Link
                      to={`/forge/admin/master-label?in=${encodeURIComponent(row.masterLabelItemNumber)}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--accent)' }}
                    >
                      Open
                      <OpenInNewIcon style={{ fontSize: 15 }} />
                    </Link>
                  ) : (
                    <span style={{ color: 'var(--text-tertiary)' }}>Unavailable</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const HoverMetricRow: React.FC<{
  label: string;
  metric: DailyReportMetric;
  infoContent?: React.ReactNode;
}> = ({ label, metric, infoContent }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, padding: '8px 0', borderBottom: '1px solid var(--divider)' }}>
    <SectionLabel label={label} infoContent={infoContent} />
    <div style={{ ...mono, color: 'var(--text-secondary)', fontSize: 12 }}>
      {formatNumber(metric.count)} · {formatPercent(metric.pct)}
    </div>
  </div>
);

const HoverMetricCard: React.FC<{
  label: string;
  metric: DailyReportMetric;
  detail: string;
  infoContent?: React.ReactNode;
}> = ({ label, metric, detail, infoContent }) => (
  <div style={{ padding: 14, borderRadius: 10, background: 'var(--elevated)' }}>
    <div style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span>{label}</span>
      {infoContent ? <InfoHint content={infoContent} /> : null}
    </div>
    <div style={{ marginTop: 8, fontSize: 24, fontWeight: 800 }}>{formatNumber(metric.count)}</div>
    <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{detail}</div>
  </div>
);

export const DailyReportDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();

  const [date, setDate] = useState(getTodayYmd);
  const [chainOptions, setChainOptions] = useState<ChainOption[]>([]);
  const [selectedChainId, setSelectedChainId] = useState<number | null>(loadStoredChainId);
  const [snapshot, setSnapshot] = useState<DailyReportSnapshot | null>(null);
  const [isLoadingChains, setIsLoadingChains] = useState(true);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [progressStartedAt, setProgressStartedAt] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [estimatedDurationMs, setEstimatedDurationMs] = useState(DEFAULT_ESTIMATED_DURATION_MS);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [sortKey, setSortKey] = useState<StoreSortKey>('whitelistedModPct');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    let cancelled = false;

    const loadChains = async () => {
      setIsLoadingChains(true);
      try {
        const stores = await ApiService.fetchStores(token);
        if (cancelled) return;
        const chainsById = stores.reduce<Map<number, ChainOption>>((acc, store: Store) => {
          if (!acc.has(store.chainId)) {
            acc.set(store.chainId, {
              chainId: store.chainId,
              chainName: store.chainLegibleName || store.chainName,
              timeZone: store.timeZone,
            });
          }
          return acc;
        }, new Map());
        const nextOptions = [...chainsById.values()]
          .filter((option) => SUPPORTED_CHAIN_IDS.has(option.chainId))
          .sort((a, b) => a.chainName.localeCompare(b.chainName));
        setChainOptions(nextOptions);

        const storedChainId = loadStoredChainId();
        const resolvedChainId =
          nextOptions.find((option) => option.chainId === storedChainId)?.chainId ?? nextOptions[0]?.chainId ?? null;
        setSelectedChainId(resolvedChainId);
        if (resolvedChainId !== null) {
          saveStoredChainId(resolvedChainId);
        }
      } catch (error) {
        if (!cancelled) {
          showErrorToast(error instanceof Error ? error.message : 'Error loading chains');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingChains(false);
        }
      }
    };

    loadChains();
    return () => {
      cancelled = true;
    };
  }, [showErrorToast, token]);

  useEffect(() => {
    if (!selectedChainId) return;
    setEstimatedDurationMs(estimateDurationMs(loadChainDurationMs(selectedChainId)));
  }, [selectedChainId]);

  useEffect(() => {
    if (!selectedChainId) {
      setSnapshot(null);
      setEstimatedDurationMs(DEFAULT_ESTIMATED_DURATION_MS);
      return;
    }

    let cancelled = false;

    const loadSnapshot = async () => {
      setIsLoadingSnapshot(true);
      try {
        const nextSnapshot = await ForgeApiService.fetchDailyReport(token, selectedChainId, date);
        if (!cancelled) {
          setSnapshot(nextSnapshot);
          const storedDurationMs = loadChainDurationMs(selectedChainId);
          const preferredDurationMs = Math.max(
            nextSnapshot.snapshot.computeDurationMs ?? 0,
            storedDurationMs ?? 0,
          );
          if (preferredDurationMs > 0) {
            saveChainDurationMs(selectedChainId, preferredDurationMs);
          }
          setEstimatedDurationMs(estimateDurationMs(preferredDurationMs || null));
        }
      } catch (error) {
        if (!cancelled) {
          if (error instanceof ForgeApiError && error.status === 404) {
            setSnapshot(null);
            setEstimatedDurationMs(estimateDurationMs(loadChainDurationMs(selectedChainId)));
          } else {
            showErrorToast(error instanceof Error ? error.message : 'Error loading daily report');
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSnapshot(false);
        }
      }
    };

    loadSnapshot();
    return () => {
      cancelled = true;
    };
  }, [date, selectedChainId, showErrorToast, token]);

  useEffect(() => {
    if (progressStartedAt === null) return;

    const updateElapsed = () => {
      setElapsedMs(Date.now() - progressStartedAt);
    };

    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 250);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [progressStartedAt]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const handleRefresh = async () => {
    if (!selectedChainId) return;
    const startedAt = Date.now();
    setIsRefreshing(true);
    setProgressStartedAt(startedAt);
    setElapsedMs(0);
    try {
      const nextSnapshot = await ForgeApiService.refreshDailyReport(token, selectedChainId, date);
      setSnapshot(nextSnapshot);
      const actualDurationMs = Date.now() - startedAt;
      const completedDurationMs = Math.max(nextSnapshot.snapshot.computeDurationMs ?? 0, actualDurationMs);
      saveChainDurationMs(selectedChainId, completedDurationMs);
      setEstimatedDurationMs(estimateDurationMs(completedDurationMs));
      showSuccessToast('Daily snapshot refreshed');
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error refreshing snapshot');
    } finally {
      setIsRefreshing(false);
      setProgressStartedAt(null);
      setElapsedMs(0);
    }
  };

  const handleChainChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextChainId = Number(event.target.value);
    setSelectedChainId(nextChainId);
    saveStoredChainId(nextChainId);
  };

  const sortedStoreRows = useMemo(() => {
    if (!snapshot) return [];
    const rows = [...snapshot.storeRows];
    rows.sort((a, b) => {
      let comparison = 0;
      switch (sortKey) {
        case 'storeName':
          comparison = a.storeName.localeCompare(b.storeName);
          break;
        case 'eligibleMods':
          comparison = a.eligibleMods - b.eligibleMods;
          break;
        case 'whitelistedMods':
          comparison = a.whitelistedMods - b.whitelistedMods;
          break;
        case 'whitelistedModPct':
          comparison = a.whitelistedModPct - b.whitelistedModPct;
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return rows;
  }, [snapshot, sortDirection, sortKey]);

  const toggleSort = (nextKey: StoreSortKey) => {
    if (sortKey === nextKey) {
      setSortDirection((value) => (value === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(nextKey);
    setSortDirection(nextKey === 'storeName' ? 'asc' : 'desc');
  };

  const selectedChain = chainOptions.find((option) => option.chainId === selectedChainId) ?? null;
  const snapshotMatchesSelection =
    snapshot !== null &&
    snapshot.chain.chainId === selectedChainId &&
    snapshot.snapshot.selectedDate === date;
  const selectedChainTimeZone = snapshotMatchesSelection
    ? snapshot.chain.timeZone
    : selectedChain?.timeZone ?? 'UTC';
  const currentChainDate = getYmdInTimeZone(currentTime, selectedChainTimeZone);
  const isSelectedDateTodayInChain = snapshotMatchesSelection
    ? snapshot.snapshot.selectedDateIsTodayInChain ?? date === currentChainDate
    : date === currentChainDate;
  const currentChainTimeLabel = formatCurrentTimeInTimeZone(currentTime, selectedChainTimeZone);
  const liveAgeSeconds =
    snapshotMatchesSelection && snapshot
      ? Math.max(0, Math.floor((currentTime.getTime() - new Date(snapshot.snapshot.computedAtUtc).getTime()) / 1000))
      : 0;
  const cropDiagnosticRows = snapshot
    ? [
        {
          label: 'Active crops',
          metric: snapshot.cropDiagnostics.activeCrops,
          infoContent: <div>Percentage uses {formatNumber(snapshot.cropDiagnostics.totalCrops)} total crops as the denominator.</div>,
        },
        {
          label: 'Inactive crops',
          metric: snapshot.cropDiagnostics.inactiveCrops,
          infoContent: <div>Percentage uses {formatNumber(snapshot.cropDiagnostics.totalCrops)} total crops as the denominator.</div>,
        },
        {
          label: 'Pending crops',
          metric: snapshot.cropDiagnostics.pendingCrops,
          infoContent: <div>Percentage uses {formatNumber(snapshot.cropDiagnostics.totalCrops)} total crops as the denominator.</div>,
        },
        {
          label: 'Active front product crops',
          metric: snapshot.cropDiagnostics.activeFrontProductCrops,
          infoContent: <div>Current active product crops classified as front. Percentage uses {formatNumber(snapshot.cropDiagnostics.totalCrops)} total crops.</div>,
        },
        {
          label: 'Active non-front crops',
          metric: snapshot.cropDiagnostics.activeNonFrontCrops,
          infoContent: <div>Active crops that are not front-oriented. Percentage uses {formatNumber(snapshot.cropDiagnostics.totalCrops)} total crops.</div>,
        },
        {
          label: 'Active carton crops',
          metric: snapshot.cropDiagnostics.activeCartonCrops,
          infoContent: <div>Active crops classified as cartons. Percentage uses {formatNumber(snapshot.cropDiagnostics.totalCrops)} total crops.</div>,
        },
        {
          label: 'Front crops assigned to design',
          metric: snapshot.cropDiagnostics.activeFrontCropsAssignedToDesign,
          infoContent: <div>Active front product crops with a non-null design assignment. Percentage uses {formatNumber(snapshot.cropDiagnostics.totalCrops)} total crops.</div>,
        },
        {
          label: 'Front crops without design',
          metric: snapshot.cropDiagnostics.activeFrontCropsWithoutDesign,
          infoContent: <div>Active front product crops that still have no design assignment. Percentage uses {formatNumber(snapshot.cropDiagnostics.totalCrops)} total crops.</div>,
        },
        {
          label: 'Crops missing normalized barcode',
          metric: snapshot.cropDiagnostics.cropsMissingNormalizedBarcode,
          infoContent: <div>Crops missing a usable <span style={mono}>normalized_barcode</span>. Percentage uses {formatNumber(snapshot.cropDiagnostics.totalCrops)} total crops.</div>,
        },
        {
          label: 'Unknown UPC pending reason',
          metric: snapshot.cropDiagnostics.cropsWithUnknownUpcPendingReason,
          infoContent: <div>Pending crops whose pending reason is <span style={mono}>unknown_upc</span>. Percentage uses {formatNumber(snapshot.cropDiagnostics.totalCrops)} total crops.</div>,
        },
      ]
    : [];
  const designDiagnosticRows = snapshot
    ? [
        {
          label: 'Designs missing normalized barcode',
          metric: snapshot.designDiagnostics.designsMissingNormalizedBarcode,
          infoContent: <div>Percentage uses {formatNumber(snapshot.designDiagnostics.totalDesigns)} total designs as the denominator.</div>,
        },
        {
          label: 'Designs resolved via attached crops',
          metric: snapshot.designDiagnostics.designsResolvedViaAttachedCrops,
          infoContent: <div>Designs resolved through attached crops rather than SQL barcode ownership. Percentage uses {formatNumber(snapshot.designDiagnostics.totalDesigns)} total designs.</div>,
        },
        {
          label: 'Designs without active front crops',
          metric: snapshot.designDiagnostics.designsWithoutActiveFrontCrops,
          infoContent: <div>Designs that currently have no active front product crops. Percentage uses {formatNumber(snapshot.designDiagnostics.totalDesigns)} total designs.</div>,
        },
        {
          label: 'Active front crops linked to design barcode outside catalog',
          metric: snapshot.designDiagnostics.activeFrontCropsLinkedToDesignBarcodeOutsideCatalog,
          infoContent: <div>Percentage uses {formatNumber(snapshot.cropDiagnostics.activeFrontProductCrops.count)} active front product crops as the denominator.</div>,
        },
      ]
    : [];
  const embeddingConsistencyRows = snapshot?.embeddingConsistency
    ? [
        {
          label: 'Active front crops without embedding',
          metric: snapshot.embeddingConsistency.activeCropsWithoutEmbedding,
          infoContent: <div>Percentage uses {formatNumber(snapshot.embeddingConsistency.totalActiveFrontProductCrops)} active front product crops as the denominator.</div>,
        },
        {
          label: 'Active front crops with multiple embeddings',
          metric: snapshot.embeddingConsistency.activeCropsWithMultipleEmbeddings,
          infoContent: <div>Percentage uses {formatNumber(snapshot.embeddingConsistency.totalActiveFrontProductCrops)} active front product crops as the denominator.</div>,
        },
        {
          label: 'Embeddings with missing crop',
          metric: snapshot.embeddingConsistency.embeddingsWithMissingCrop,
          infoContent: <div>Percentage uses {formatNumber(snapshot.embeddingConsistency.totalEmbeddings)} embeddings as the denominator.</div>,
        },
        {
          label: 'Embeddings with is_active mismatch',
          metric: snapshot.embeddingConsistency.embeddingsIsActiveMismatch,
          infoContent: <div>Embeddings whose <span style={mono}>is_active</span> disagrees with the linked crop. Percentage uses {formatNumber(snapshot.embeddingConsistency.totalEmbeddings)} embeddings.</div>,
        },
        {
          label: 'Embeddings with normalized_barcode mismatch',
          metric: snapshot.embeddingConsistency.embeddingsNormalizedBarcodeMismatch,
          infoContent: <div>Embeddings whose <span style={mono}>normalized_barcode</span> disagrees with the linked crop. Percentage uses {formatNumber(snapshot.embeddingConsistency.totalEmbeddings)} embeddings.</div>,
        },
        {
          label: 'Embeddings with non-bool is_active',
          metric: snapshot.embeddingConsistency.embeddingNonBoolIsActive,
          infoContent: <div>Embeddings whose <span style={mono}>is_active</span> is not stored as a real Mongo boolean. Percentage uses {formatNumber(snapshot.embeddingConsistency.totalEmbeddings)} embeddings.</div>,
        },
      ]
    : [];

  return (
    <div className="page-content page-content--wide">
      <div className="forge-card" style={{ marginBottom: 20, padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Daily Coverage Snapshot
            </div>
            <div style={{ marginTop: 8, fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>
              {selectedChain?.chainName || 'Select a chain'}
            </div>
            <div style={{ marginTop: 10, color: 'var(--text-secondary)', maxWidth: 720 }}>
              Cache-first chain snapshot for whitelisted store and crop coverage. Historical dates are read-only cached snapshots; refresh computes the current state only.
            </div>
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4, color: 'var(--text-secondary)', fontSize: 12 }}>
              <div>Chain time zone: <span style={mono}>{selectedChainTimeZone}</span></div>
              <div>Current chain time: <span style={mono}>{currentChainTimeLabel}</span></div>
              <div>
                {isSelectedDateTodayInChain
                  ? 'Selected date is today in this chain.'
                  : 'Selected date is historical and read-only.'}
              </div>
              <div>Refresh is available only for today&apos;s date in the selected chain time zone.</div>
            </div>
          </div>
          {snapshotMatchesSelection && snapshot && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              <div style={badgeStyle(liveAgeSeconds)}>{formatRelativeAge(liveAgeSeconds)}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, textAlign: 'right' }}>
                Computed {formatTimestampUtc(snapshot.snapshot.computedAtUtc)} UTC
                <br />
                Chain time {formatTimestampInTimeZone(snapshot.snapshot.computedAtUtc, selectedChainTimeZone)}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={selectedChainId ?? ''} onChange={handleChainChange} style={controlStyle} disabled={isLoadingChains || chainOptions.length === 0}>
            {chainOptions.map((option) => (
              <option key={option.chainId} value={option.chainId}>
                {option.chainName}
              </option>
            ))}
          </select>
          <DatePicker value={date} onChange={setDate} />
          <button
            onClick={handleRefresh}
            disabled={!selectedChainId || isRefreshing || !isSelectedDateTodayInChain}
            style={{
              ...actionButtonStyle,
              opacity: !selectedChainId || isRefreshing || !isSelectedDateTodayInChain ? 0.65 : 1,
              cursor: !selectedChainId || isRefreshing || !isSelectedDateTodayInChain ? 'not-allowed' : 'pointer',
            }}
          >
            <RefreshOutlinedIcon style={{ fontSize: 16 }} />
            {isRefreshing ? 'Refreshing…' : 'Refresh Snapshot'}
          </button>
        </div>

        {isRefreshing && (
          <ProgressBanner
            elapsedMs={elapsedMs}
            estimatedDurationMs={estimatedDurationMs}
          />
        )}
      </div>

      {!isLoadingChains && !isLoadingSnapshot && !snapshotMatchesSelection && (
        <div className="forge-card" style={{ padding: 24 }}>
          <div className="forge-card-title">No Snapshot</div>
          <div style={{ color: 'var(--text-secondary)' }}>
            {chainOptions.length === 0
              ? 'No Forge-supported chains are available in the selector yet.'
              : isSelectedDateTodayInChain
                ? 'No cached snapshot exists for this chain/date yet. Use Refresh Snapshot to compute the current-state report.'
                : 'No cached historical snapshot exists for this chain/date. Historical dates are read-only.'}
          </div>
        </div>
      )}

      {!isLoadingChains && snapshotMatchesSelection && snapshot && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 18,
              marginBottom: 20,
            }}
          >
            <WhitelistedStoresTile
              whitelistedStores={snapshot.storeSummary.whitelistedStores}
              storesWithEligibleMods={snapshot.storeSummary.storesWithEligibleMods}
              totalActiveStores={snapshot.storeSummary.totalActiveStores}
            />
            <MetricTile
              icon={<ProductBoxGlyph />}
              label="Item Number Coverage"
              value={formatPercent(snapshot.coverage.itemNumbers.pct)}
              detail={`${formatNumber(snapshot.coverage.itemNumbers.covered)} covered item numbers`}
              infoContent={
                <>
                  <div>Distinct shelf item numbers from the latest whitelisted catalog.</div>
                  <div>Marked covered when any shelf barcode under that item number has an active front crop.</div>
                </>
              }
            />
            <MetricTile
              icon={<BarcodeGlyph />}
              label="Barcode Coverage"
              value={formatPercent(snapshot.coverage.barcodes.pct)}
              detail={`${formatNumber(snapshot.coverage.barcodes.covered)} covered barcodes`}
              infoContent={
                <>
                  <div>Distinct normalized barcodes from the latest whitelisted shelf catalog.</div>
                  <div>Matched using active front product crops on <span style={mono}>normalized_barcode</span>.</div>
                </>
              }
            />
            <MetricTile
              icon={<AutoAwesomeMosaicOutlinedIcon />}
              label="Design Coverage"
              value={formatPercent(snapshot.coverage.designs.pct)}
              detail={`${formatNumber(snapshot.coverage.designs.covered)} covered barcodes with assigned design`}
              infoContent={
                <>
                  <div>Uses the same whitelisted barcode set as barcode coverage.</div>
                  <div>Marked covered when an active front crop has a non-null design assignment.</div>
                </>
              }
            />
            <MetricTile
              icon={<AutoAwesomeMosaicOutlinedIcon />}
              label="Total Designs"
              value={formatNumber(snapshot.designDiagnostics.totalDesigns)}
              detail={`${snapshot.designDiagnostics.statuses.length} active design status buckets`}
              infoContent={
                <>
                  <div>Total designs currently present for the selected chain snapshot.</div>
                  <div>This is the canonical design count used throughout the report.</div>
                </>
              }
            />
          </div>

          <div style={{ ...sectionGridStyle, gridTemplateColumns: 'minmax(0, 1.2fr) minmax(320px, 0.8fr)', marginBottom: 20 }}>
            <div className="forge-card" style={{ minWidth: 0 }}>
              <div className="forge-card-title">Store Coverage Histogram</div>
              <div style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>
                X axis buckets stores by whitelisted mod percentage in 10-point increments.
              </div>
              <HistogramChart data={snapshot.modHistogram} />
            </div>

            <div className="forge-card">
              <div className="forge-card-title">Coverage Definitions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <div style={{ fontWeight: 700, width: 'fit-content' }}>
                    <SectionLabel
                      label="Whitelisted mod"
                      infoContent={<div>Evaluated directly from <span style={mono}>mod_eligibility.is_compliance_enabled = 1</span> in current state.</div>}
                    />
                  </div>
                  <div style={{ color: 'var(--text-secondary)' }}>A mod with `mod_eligibility.is_compliance_enabled = 1` in the current DB state.</div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, width: 'fit-content' }}>
                    <SectionLabel
                      label="Whitelisted store"
                      infoContent={<div>Stores must be active and have at least one whitelisted mod.</div>}
                    />
                  </div>
                  <div style={{ color: 'var(--text-secondary)' }}>An active store with at least one whitelisted mod.</div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, width: 'fit-content' }}>
                    <SectionLabel
                      label="Coverage denominator"
                      infoContent={<div>Derived from the latest whitelisted shelf catalog only, not the full chain catalog.</div>}
                    />
                  </div>
                  <div style={{ color: 'var(--text-secondary)' }}>Distinct item numbers and normalized barcodes from the latest whitelisted shelf catalog.</div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, width: 'fit-content' }}>
                    <SectionLabel
                      label="Coverage source"
                      infoContent={<div>Uses active front product crops matched on <span style={mono}>normalized_barcode</span>. Embeddings remain diagnostic-only.</div>}
                    />
                  </div>
                  <div style={{ color: 'var(--text-secondary)' }}>Current active front product crops matched by `normalized_barcode`. Embeddings are shown separately as consistency diagnostics.</div>
                </div>
                <div style={{ ...mono, color: 'var(--text-secondary)', fontSize: 12 }}>
                  Item numbers: {formatCoverage(snapshot.coverage.itemNumbers)}
                  <br />
                  Barcodes: {formatCoverage(snapshot.coverage.barcodes)}
                  <br />
                  Designs: {formatCoverage(snapshot.coverage.designs)}
                </div>
              </div>
            </div>
          </div>

          <div style={{ ...sectionGridStyle, marginBottom: 20 }}>
            {snapshot.sectionSplit && (
              <div className="forge-card">
                <div className="forge-card-title">Section Split</div>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>
                  Mutually exclusive crop buckets matching the current Master Label section layout.
                </div>
                <SectionSplitChart sectionSplit={snapshot.sectionSplit} />
              </div>
            )}

            <div className="forge-card">
              <div className="forge-card-title">Crop Diagnostics</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {cropDiagnosticRows.map((item) => (
                  <HoverMetricRow
                    key={item.label}
                    label={item.label}
                    metric={item.metric}
                    infoContent={item.infoContent}
                  />
                ))}
              </div>
            </div>

            <div className="forge-card">
              <div className="forge-card-title">Source Breakdown</div>
              <div style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>
                Distribution of ProductEmbeddingCrops in the snapshot, including Forge-origin breakdown where applicable.
              </div>
              <SourceBreakdown items={snapshot.cropDiagnostics.sourceDistribution} />
            </div>
          </div>

          <div style={{ ...sectionGridStyle, marginBottom: 20 }}>
            <div className="forge-card">
              <div className="forge-card-title">Barcode Diagnostics</div>
              <div style={{ display: 'grid', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                  <HoverMetricCard
                    label="No Item Number"
                    metric={snapshot.barcodeDiagnostics.noItemNumberFound}
                    detail={`${formatPercent(snapshot.barcodeDiagnostics.noItemNumberFound.pct)} of distinct cropped barcodes`}
                    infoContent={<div>Percentage uses {formatNumber(snapshot.barcodeDiagnostics.distinctCroppedBarcodes)} distinct cropped barcodes as the denominator.</div>}
                  />
                  <HoverMetricCard
                    label="Multi Item Number"
                    metric={snapshot.barcodeDiagnostics.multipleItemNumbersFound}
                    detail={`${formatPercent(snapshot.barcodeDiagnostics.multipleItemNumbersFound.pct)} ambiguous ownership`}
                    infoContent={<div>Barcodes that resolve to more than one candidate item number across the metadata stores.</div>}
                  />
                  <HoverMetricCard
                    label="Outside Catalog"
                    metric={snapshot.barcodeDiagnostics.outsideWhitelistedCatalog}
                    detail={`${formatPercent(snapshot.barcodeDiagnostics.outsideWhitelistedCatalog.pct)} outside denominator`}
                    infoContent={<div>Distinct cropped barcodes that do not exist in the whitelisted shelf denominator.</div>}
                  />
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                  Distinct cropped barcodes inspected: <span style={mono}>{formatNumber(snapshot.barcodeDiagnostics.distinctCroppedBarcodes)}</span>
                </div>
              </div>
            </div>

            <div className="forge-card">
              <div className="forge-card-title">Design Diagnostics</div>
              <div style={{ marginBottom: 14 }}>
                <HorizontalMetricBars items={snapshot.designDiagnostics.statuses} color="linear-gradient(90deg, var(--blue), var(--purple))" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {designDiagnosticRows.map((item) => (
                  <HoverMetricRow
                    key={item.label}
                    label={item.label}
                    metric={item.metric}
                    infoContent={item.infoContent}
                  />
                ))}
              </div>
            </div>
          </div>

          {snapshot.embeddingConsistency && (
            <div className="forge-card" style={{ marginBottom: 20 }}>
              <div className="forge-card-title">Embedding Consistency</div>
              <div style={{ color: 'var(--text-secondary)', marginBottom: 14 }}>
                Slower, deeper current-state diagnostics between active front product crops and `ProductEmbeddings` by `product_embedding_crop_id`.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {embeddingConsistencyRows.map((item) => (
                  <HoverMetricRow
                    key={item.label}
                    label={item.label}
                    metric={item.metric}
                    infoContent={item.infoContent}
                  />
                ))}
              </div>
            </div>
          )}

          <div style={{ ...sectionGridStyle, marginBottom: 20 }}>
            <ExampleTable title="Examples · No Item Number Found" rows={snapshot.examples.noItemNumberFound} />
            <ExampleTable title="Examples · Multiple Item Numbers" rows={snapshot.examples.multipleItemNumbersFound} />
            <ExampleTable title="Examples · Outside Whitelisted Catalog" rows={snapshot.examples.outsideWhitelistedCatalog} />
          </div>

          <div className="forge-card">
            <div className="forge-card-title">Store Breakdown</div>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>
                      <button style={tableHeaderButtonStyle} onClick={() => toggleSort('storeName')}>
                        Store
                      </button>
                    </th>
                    <th>
                      <button style={tableHeaderButtonStyle} onClick={() => toggleSort('eligibleMods')}>
                        Eligible Mods
                      </button>
                    </th>
                    <th>
                      <button style={tableHeaderButtonStyle} onClick={() => toggleSort('whitelistedMods')}>
                        Whitelisted Mods
                      </button>
                    </th>
                    <th>
                      <button style={tableHeaderButtonStyle} onClick={() => toggleSort('whitelistedModPct')}>
                        Whitelist %
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStoreRows.map((row: DailyReportStoreRow) => (
                    <tr key={row.storeId}>
                      <td>{row.storeName}</td>
                      <td style={mono}>{formatNumber(row.eligibleMods)}</td>
                      <td style={mono}>{formatNumber(row.whitelistedMods)}</td>
                      <td style={{ ...mono, color: row.whitelistedModPct >= 80 ? 'var(--positive)' : row.whitelistedModPct >= 40 ? 'var(--warning)' : 'var(--danger)' }}>
                        {formatPercent(row.whitelistedModPct)}
                      </td>
                    </tr>
                  ))}
                  {sortedStoreRows.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ color: 'var(--text-secondary)' }}>
                        No eligible stores found for the selected chain/date.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
